import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import PermissionGate from "@/components/PermissionGate";
import AppLayout from "@/components/AppLayout";
import { PageLoader, RequestActivityIndicator } from '@/components/LoadingFeedback';

import AuthCallback from "@/pages/AuthCallback";

const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Certified = lazy(() => import("@/pages/Certified"));
const CertifiedSummary = lazy(() => import("@/pages/CertifiedSummary"));
const Loose = lazy(() => import("@/pages/Loose"));
const Purchases = lazy(() => import("@/pages/Purchases"));
const PurchaseEditor = lazy(() => import("@/pages/PurchaseEditor"));
const PurchaseDetail = lazy(() => import("@/pages/PurchaseDetail"));
const SupplierReturns = lazy(() => import("@/pages/SupplierReturns"));
const SupplierReturnEditor = lazy(() => import("@/pages/SupplierReturnEditor"));
const Sales = lazy(() => import("@/pages/Sales"));
const SalesAgents = lazy(() => import("@/pages/SalesAgents"));
const Sold = lazy(() => import("@/pages/Sold"));
const SaleEditor = lazy(() => import("@/pages/SaleEditor"));
const SaleDetail = lazy(() => import("@/pages/SaleDetail"));
const Production = lazy(() => import("@/pages/Production"));
const Jewelry = lazy(() => import("@/pages/JewelryWorkspace"));
const JewelryStock = lazy(() => import("@/pages/JewelryStock"));
const JewelryDuplicateIds = lazy(() => import("@/pages/JewelryDuplicateIds"));
const ItemNames = lazy(() => import("@/pages/ItemNames"));
const JewelrySets = lazy(() => import("@/pages/JewelrySets"));
const CatawikiBaskets = lazy(() => import("@/pages/CatawikiBaskets"));
const CatawikiPhotography = lazy(() => import("@/pages/CatawikiPhotography"));
const CatawikiListing = lazy(() => import("@/pages/CatawikiListing"));
const CatawikiSubmitted = lazy(() => import("@/pages/CatawikiSubmitted"));
const CatawikiShipping = lazy(() => import("@/pages/CatawikiShipping"));
const CatawikiProfit = lazy(() => import("@/pages/CatawikiProfit"));
const CatawikiPayments = lazy(() => import("@/pages/CatawikiPayments"));
const Documents = lazy(() => import("@/pages/Documents"));
const JewelryImport = lazy(() => import("@/pages/JewelryImport"));
const GoldStock = lazy(() => import("@/pages/GoldStock"));
const StockTake = lazy(() => import("@/pages/StockTake"));
const JewelryImageLibrary = lazy(() => import("@/pages/JewelryImageLibrary"));
const Reorder = lazy(() => import("@/pages/Reorder"));
const Inquiries = lazy(() => import("@/pages/Inquiries"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const Reports = lazy(() => import("@/pages/Reports"));
const CurrentStock = lazy(() => import("@/pages/CurrentStock"));
const Team = lazy(() => import("@/pages/Team"));
const Settings = lazy(() => import("@/pages/Settings"));
const AuditFeed = lazy(() => import("@/pages/AuditFeed"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const LoginActivity = lazy(() => import("@/pages/LoginActivity"));
const Invite = lazy(() => import("@/pages/Invite"));
const PublicCatalogue = lazy(() => import("@/pages/PublicCatalogue"));
const PublicOffer = lazy(() => import("@/pages/PublicOffer"));

function RouteFallback() {
  return <PageLoader fullScreen />;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of session_id in URL fragment (CRITICAL: must be before ProtectedRoute)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<Invite />} />
      <Route path="/catalogue/:token" element={<PublicCatalogue />} />
      <Route path="/offer/:token" element={<PublicOffer />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/certified" element={<Certified />} />
        <Route path="/certified/summary" element={<CertifiedSummary />} />
        <Route path="/loose" element={<Loose />} />
        <Route path="/purchases" element={<PermissionGate perm="purchases"><Purchases /></PermissionGate>} />
        <Route path="/supplier-returns" element={<PermissionGate perm="purchases"><SupplierReturns /></PermissionGate>} />
        <Route path="/supplier-returns/new" element={<PermissionGate perm="purchases"><SupplierReturnEditor /></PermissionGate>} />
        <Route path="/reorder" element={<PermissionGate perm="purchases"><Reorder /></PermissionGate>} />
        <Route path="/inquiries" element={<PermissionGate perm="sales"><Inquiries /></PermissionGate>} />
        <Route path="/purchases/new" element={<PermissionGate perm="purchases"><PurchaseEditor /></PermissionGate>} />
        <Route path="/purchases/:id/edit" element={<PermissionGate perm="purchases"><PurchaseEditor /></PermissionGate>} />
        <Route path="/purchases/:id" element={<PermissionGate perm="purchases"><PurchaseDetail /></PermissionGate>} />
        <Route path="/sales" element={<PermissionGate perm="sales"><Sales /></PermissionGate>} />
        <Route path="/sales-agents" element={<PermissionGate perm="sales"><SalesAgents /></PermissionGate>} />
        <Route path="/sold" element={<PermissionGate perm="sales"><Sold /></PermissionGate>} />
        <Route path="/sales/new" element={<PermissionGate perm="sales"><SaleEditor /></PermissionGate>} />
        <Route path="/sales/:id/edit" element={<PermissionGate perm="sales"><SaleEditor /></PermissionGate>} />
        <Route path="/sales/:id" element={<PermissionGate perm="sales"><SaleDetail /></PermissionGate>} />
        <Route path="/production" element={<PermissionGate perm="workshop"><Production /></PermissionGate>} />
        <Route path="/jewelry" element={<PermissionGate perm="workshop"><Jewelry /></PermissionGate>} />
        <Route path="/jewelry-stock" element={<JewelryStock />} />
        <Route path="/jewelry-duplicate-ids" element={<JewelryDuplicateIds />} />
        <Route path="/item-names" element={<ItemNames />} />
        <Route path="/jewelry-sets" element={<JewelrySets />} />
        <Route path="/jewelry-import" element={<PermissionGate perm="workshop"><JewelryImport /></PermissionGate>} />
        <Route path="/gold-stock" element={<PermissionGate perm="workshop"><GoldStock /></PermissionGate>} />
        <Route path="/stocktake" element={<PermissionGate perm="workshop"><StockTake /></PermissionGate>} />
        <Route path="/jewelry-library" element={<PermissionGate perm="workshop"><JewelryImageLibrary /></PermissionGate>} />
        <Route path="/catawiki/baskets" element={<PermissionGate perm="catawiki"><CatawikiBaskets /></PermissionGate>} />
        <Route path="/catawiki/photography" element={<PermissionGate perm="catawiki"><CatawikiPhotography /></PermissionGate>} />
        <Route path="/catawiki/listing" element={<PermissionGate perm="catawiki"><CatawikiListing /></PermissionGate>} />
        <Route path="/catawiki/submitted" element={<PermissionGate perm="catawiki"><CatawikiSubmitted /></PermissionGate>} />
        <Route path="/catawiki/shipping" element={<PermissionGate perm="catawiki"><CatawikiShipping /></PermissionGate>} />
        <Route path="/catawiki/profit" element={<PermissionGate perm="accounting"><CatawikiProfit /></PermissionGate>} />
        <Route path="/catawiki/payments" element={<PermissionGate perm="accounting"><CatawikiPayments /></PermissionGate>} />
        <Route path="/documents" element={<PermissionGate perm="catawiki"><Documents /></PermissionGate>} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/reports" element={<PermissionGate perm="accounting"><Reports /></PermissionGate>} />
        <Route path="/stock-reports" element={<PermissionGate perm="workshop"><CurrentStock /></PermissionGate>} />
        <Route path="/team" element={<Team />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/audit" element={<AuditFeed />} />
        <Route path="/login-activity" element={<LoginActivity />} />
      </Route>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  const isLight = theme === "light";
  return (
    <Toaster
      theme={isLight ? "light" : "dark"}
      position="top-right"
      toastOptions={{
        style: {
          background: isLight ? "#FFFFFF" : "#111520",
          border: `1px solid ${isLight ? "#E2E8F0" : "#1E293B"}`,
          color: isLight ? "#0F172A" : "#F8FAFC",
        },
      }}
    />
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <ConfirmProvider>
              <Suspense fallback={<RouteFallback />}>
                <AppRouter />
              </Suspense>
              <ThemedToaster />
              <RequestActivityIndicator />
            </ConfirmProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
